import React, { useState } from 'react';
import { FormikErrors } from 'formik';
import { Box, Field, Portal, Combobox, useFilter, useListCollection, Highlight, useComboboxContext } from '@chakra-ui/react';
import { TABLE_FILTERS, TableFilter, FiltersFormData, TableSelector } from './TableFiltersForm';

interface FilterAutocompleteProps {
    selector: TableSelector
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void> | Promise<FormikErrors<FiltersFormData>>
    submitForm: () => Promise<any>;
    errors: FormikErrors<FiltersFormData>;
    values: FiltersFormData;
    items: any;
    disabled?: boolean;
}

const FilterAutocomplete = ({ 
    selector,
    setFieldValue,
    submitForm,
    errors,
    values,
    items,
    disabled = false,
}: FilterAutocompleteProps) => {
  const { contains } = useFilter({ sensitivity: "base" })

  const { label } = TABLE_FILTERS[selector] as TableFilter;

  
  const { collection, filter } = useListCollection({
    initialItems: items,
    filter: contains,
  });

  const [inputValue, setInputValue] = useState('');
  const hasSearchQuery = inputValue.trim().length >= 1;

  return (
    <Field.Root invalid={!!errors[selector]}>
        <Field.Label>{label}</Field.Label>
        <Combobox.Root
            collection={collection}
            disabled={disabled}
            onInputValueChange={(e: { inputValue: string }) => {
              setInputValue(e.inputValue);
              filter(e.inputValue);
            }}
            value={values[selector]}
            onValueChange={(e: any) => {
                setFieldValue(selector, e?.value);
                submitForm();
            }}
            inputBehavior="autohighlight"
            openOnClick={true}
        >
          <Combobox.Control>
            <Combobox.Input placeholder="Type to search" />
            <Combobox.IndicatorGroup>
              <Combobox.ClearTrigger />
              <Combobox.Trigger />
            </Combobox.IndicatorGroup>
          </Combobox.Control>

          <Portal>
            <Combobox.Positioner>
              <Combobox.Content maxH="min(280px, 50vh)" overflowY="auto">
                {!hasSearchQuery ? (
                  <Box px={3} py={2} fontSize="sm" color="fg.muted">
                    Start typing to filter results
                  </Box>
                ) : collection?.items?.length === 0 ? (
                  <Combobox.Empty>No items found</Combobox.Empty>
                ) : (
                  collection?.items?.map((item: any, j: number) => (
                    <ComboboxItem item={item} key={j} />
                  ))
                )}
              </Combobox.Content>
            </Combobox.Positioner>
          </Portal>
        </Combobox.Root>
    </Field.Root>
  )
}

function ComboboxItem(props: { item: { label: string; value: string } }) {
    const { item } = props
    const combobox = useComboboxContext()
    return (
      <Combobox.Item item={item} key={item.value}>
        <Combobox.ItemText>
          <Highlight
            ignoreCase
            query={combobox.inputValue}
            styles={{ bg: "brand.emphasized", fontWeight: "medium" }}
          >
            {item.label}
          </Highlight>
        </Combobox.ItemText>
      </Combobox.Item>
    )
  }

export default FilterAutocomplete;

